import { useEffect, useRef, useState } from 'react';
import { DELETION_REASONS, isSoftDeleteReason } from '@wineryxplorer/shared';
import type { DeletionReason } from '@wineryxplorer/shared';
import { trpc } from '../../lib/trpc';
import { Button } from '../../components/ui/Button';
import { Typography } from '../../components/ui/Typography';

export type DeleteOutcome = 'wiped' | 'pending_deletion';

interface DeleteUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  onDeleted: (outcome: DeleteOutcome) => void;
}

const REASON_COPY: Record<DeletionReason, { label: string; helper: string }> = {
  test_account: { label: 'Test account', helper: 'Deleted now. Cannot be undone.' },
  tos_violation: {
    label: 'Violated TOS',
    helper: 'Hidden now and email banned. Recoverable for 30 days, then you can purge it.',
  },
  spam: {
    label: 'Spam',
    helper: 'Hidden now and email banned. Recoverable for 30 days, then you can purge it.',
  },
  other: { label: 'Other', helper: 'Deleted now. Cannot be undone. Add a note.' },
};

const normalize = (value: string) => value.trim().toLowerCase();

const DeleteUserDialogBody = ({
  onClose,
  userId,
  userEmail,
  onDeleted,
}: Omit<DeleteUserDialogProps, 'isOpen'>) => {
  const utils = trpc.useUtils();
  const [reason, setReason] = useState<DeletionReason | null>(null);
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState('');

  const preview = trpc.adminUsers.deletePreview.useQuery({ id: userId });

  const deleteMutation = trpc.adminUsers.delete.useMutation({
    onSuccess: (result) => {
      utils.adminUsers.list.invalidate();
      if (result.outcome === 'pending_deletion') {
        utils.adminUsers.getById.invalidate({ id: userId });
      }
      onDeleted(result.outcome);
    },
  });

  const dialogRef = useRef<HTMLDivElement>(null);
  const isPending = deleteMutation.isPending;

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPending, onClose]);

  const trimmedNote = note.trim();
  const isReady =
    reason !== null &&
    (reason !== 'other' || trimmedNote.length > 0) &&
    normalize(confirm) === normalize(userEmail);
  const isSoft = reason !== null && isSoftDeleteReason(reason);
  const data = preview.data;

  const handleDelete = () => {
    if (!isReady || reason === null) return;
    deleteMutation.mutate({
      id: userId,
      reason,
      note: trimmedNote || undefined,
      confirm,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !deleteMutation.isPending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Delete account"
        className="bg-gray-50 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-3">
          <Typography variant="heading3" as="h3">
            Delete account
          </Typography>
          <div className="flex items-center gap-3">
            {deleteMutation.error && (
              <span role="alert" className="text-sm text-error">
                {deleteMutation.error.message}
              </span>
            )}
            <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={!isReady}
              loading={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : isSoft ? 'Hide and ban' : 'Delete account'}
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-gray-700">
            This permanently deletes {userEmail} and everything they&apos;ve posted. It can&apos;t
            be undone after the account is purged.
          </p>

          {preview.isLoading && <p className="text-sm text-gray-500">Counting their content…</p>}
          {preview.error && (
            <p role="alert" className="text-sm text-error">
              {preview.error.message}
            </p>
          )}
          {data && (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                {`${data.publicReviews + data.itineraryReviews} reviews · ${data.comments} comments · ${data.ownedItineraries} itineraries · ${data.media} images`}
              </p>
              {data.curatedItineraries > 0 && (
                <p className="text-sm text-gray-700">
                  {`${data.curatedItineraries} itineraries this user wrote for WineryXplorer or a venue will be kept and credited to you.`}
                </p>
              )}
            </div>
          )}

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-gray-700 mb-1">Reason</legend>
            {DELETION_REASONS.map((value) => (
              <div key={value} className="flex items-start gap-3">
                <input
                  type="radio"
                  id={`delete-reason-${value}`}
                  name="delete-reason"
                  checked={reason === value}
                  onChange={() => setReason(value)}
                  aria-describedby={`delete-reason-${value}-helper`}
                  className="mt-1"
                />
                <div>
                  <label htmlFor={`delete-reason-${value}`} className="text-sm text-gray-900">
                    {REASON_COPY[value].label}
                  </label>
                  <p id={`delete-reason-${value}-helper`} className="text-xs text-gray-500">
                    {REASON_COPY[value].helper}
                  </p>
                </div>
              </div>
            ))}
          </fieldset>

          {reason === 'other' && (
            <div>
              <label htmlFor="delete-note" className="block text-sm font-medium text-gray-700 mb-1">
                Note
              </label>
              <textarea
                id="delete-note"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none"
                rows={3}
              />
            </div>
          )}

          <div>
            <label
              htmlFor="delete-confirm"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Type {userEmail} to confirm
            </label>
            <input
              id="delete-confirm"
              type="text"
              value={confirm}
              autoComplete="off"
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Mounted only while open, so reason, note, typed email and errors reset on close. */
export const DeleteUserDialog = ({ isOpen, ...rest }: DeleteUserDialogProps) =>
  isOpen ? <DeleteUserDialogBody {...rest} /> : null;
